import { useEffect, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { getUnitPrice } from '../../utils/utils.ts';
import { cloudinaryImage } from '../../utils/cloudinary.ts';
import '../product-list/product-list.scss';
import {
  Product,
  ProductOption,
  ProductOptionValues,
  purchaseOptions,
} from '../ProductType.ts';
import { getWeightLabel } from '../utils.ts';
import './product-item.scss';

interface ProductRowProps {
  product: Product;
  handleCartItemChange: (
    id: string,
    purchaseOption: ProductOptionValues,
    quantity: number,
    productPrice: number
  ) => void;
  handleAddToCart: (product: Product) => void;
  /** Claves `${product.id}::${purchaseOption}` que ya están en el carrito. */
  cartOptionKeys: Set<string>;
}

const ProductRow = ({
  product,
  handleCartItemChange,
  handleAddToCart,
  cartOptionKeys,
}: ProductRowProps) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [purchaseOption, setSelectedOption] = useState<ProductOption | null>(
    null
  );

  const filteredOptions: ProductOption[] = (
    typeof product.purchaseOptions === 'string'
      ? product.purchaseOptions.split(',')
      : product.purchaseOptions
  )
    .map((opt) => opt.trim().toLowerCase())
    .filter((opt) =>
      Object.values(ProductOptionValues).includes(opt as ProductOptionValues)
    )
    .map((opt) => purchaseOptions.find((option) => option.value === opt))
    .filter((opt): opt is ProductOption => opt !== undefined);

  // Fallback al primer valor para que el precio, el peso y el select no
  // parpadeen entre el primer render y el efecto de inicialización.
  const selectedOption = purchaseOption ?? filteredOptions[0] ?? null;

  useEffect(() => {
    if (filteredOptions.length > 0 && !purchaseOption) {
      setSelectedOption(filteredOptions[0]);
    }
  }, [filteredOptions, purchaseOption]);

  useEffect(() => {
    if (filteredOptions.length > 0) {
      handleCartItemChange(
        product.id,
        filteredOptions[0].value as ProductOptionValues,
        1,
        filteredOptions[0].value === ProductOptionValues.HALF_CRATE
          ? parseInt(product.price) / 2
          : parseInt(product.price)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptionChange = (selected: ProductOption | null) => {
    if (!selected) return;
    setSelectedOption(selected);

    const productPrice =
      selected.value === ProductOptionValues.HALF_CRATE
        ? parseInt(product.price) / 2
        : parseInt(product.price);

    handleCartItemChange(
      product.id,
      selected.value as ProductOptionValues,
      quantity,
      productPrice
    );
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);

      if (!selectedOption) {
        console.error('Purchase option is missing!');
        return;
      }

      handleCartItemChange(
        product.id,
        selectedOption.value,
        newQuantity,
        selectedOption.value === ProductOptionValues.HALF_CRATE
          ? parseInt(product.price) / 2
          : parseInt(product.price)
      );
    }
  };

  const outOfStock = product.hasStock === false;

  const unitPrice = getUnitPrice(
    product.price,
    selectedOption?.value as ProductOptionValues,
    product.productType,
    product.specialPrices
  );

  const weightLabel = getWeightLabel(
    product.boxWeight,
    Number(quantity),
    selectedOption?.value as ProductOptionValues,
    product.productType
  );
  // En unit/paquete/kg/gr el peso repite la cantidad o la presentación;
  // solo mostramos el peso cuando agrega información (cajón, litros, maple).
  const weightAddsInfo =
    selectedOption?.value !== undefined &&
    ![
      ProductOptionValues.UNIT,
      ProductOptionValues.PACK,
      ProductOptionValues.KG,
      ProductOptionValues.GR,
    ].includes(selectedOption.value);

  const weightDisplay = weightAddsInfo
    ? weightLabel
        .replace(/^peso:\s*/i, '')
        .replace(/\bpaquete\b/gi, 'paq.')
        .replace(/\bunidad\b/gi, 'un.')
    : '';

  const inCart =
    selectedOption !== null &&
    cartOptionKeys.has(`${product.id}::${selectedOption.value}`);

  const presentationLabel =
    selectedOption?.label ?? 'Consultar presentación';

  return (
    <div
      className={`product-card ${outOfStock ? 'out-of-stock' : ''} ${
        inCart ? 'in-cart' : ''
      }`}
    >
      {outOfStock && <div className="stock-badge">Sin stock</div>}
      {!outOfStock && inCart && (
        <div className="cart-badge-card">✓ En carrito</div>
      )}

      <img
        src={cloudinaryImage(product.image_url || product.image, 400)}
        alt={product.name}
        className="product-image"
        loading="lazy"
        decoding="async"
      />

      <h3 className="product-card-title">{product.name}</h3>

      <div className="product-card__meta">
        <p className="product-price">
          <span className="price-symbol">$</span>
          {(unitPrice * Number(quantity)).toLocaleString('es-AR')}
        </p>
        {weightDisplay && (
          <span className="product-weight" title={weightLabel}>
            {weightDisplay}
          </span>
        )}
      </div>

      <div className="product-card__footer">
        <div className="product-card__presentation">
          {filteredOptions.length > 1 ? (
            <>
              <select
                className="product-card__presentation-select"
                value={selectedOption?.value ?? ''}
                onChange={(event) => {
                  const next = filteredOptions.find(
                    (option) => option.value === event.target.value
                  );
                  if (next) handleOptionChange(next);
                }}
                disabled={outOfStock}
                aria-label="Presentación"
              >
                {filteredOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FiChevronDown
                className="product-card__presentation-chevron"
                aria-hidden="true"
              />
            </>
          ) : (
            <span
              className="product-card__presentation-value"
              title={presentationLabel}
            >
              {presentationLabel}
            </span>
          )}
        </div>

        <div className="div-quantity-buttons">
          <button
            type="button"
            className="quantity-button"
            onClick={() => handleQuantityChange(quantity - 1)}
            disabled={outOfStock || quantity <= 1}
            aria-label={`Restar una unidad de ${product.name}`}
          >
            −
          </button>
          <span className="quantity-display" aria-live="polite">
            {quantity}
          </span>
          <button
            type="button"
            className="quantity-button"
            onClick={() => handleQuantityChange(quantity + 1)}
            disabled={outOfStock}
            aria-label={`Sumar una unidad de ${product.name}`}
          >
            +
          </button>
        </div>

        <button
          type="button"
          className="add-to-cart"
          onClick={() => !outOfStock && handleAddToCart(product)}
          disabled={outOfStock}
        >
          {outOfStock
            ? 'Sin stock'
            : inCart
              ? 'Agregar otro'
              : 'Agregar al carrito'}
        </button>
      </div>
    </div>
  );
};

export default ProductRow;
